import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiMapPin, FiAlertTriangle, FiDroplet, FiWind, FiThermometer, FiShield, FiTrendingUp, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import satelliteApi from '../services/satelliteApi';
import './PropertyEvaluation.css';

const RISK_COLORS = {
  LOW: '#38a169',
  MODERATE: '#dd6b20',
  HIGH: '#e53e3e',
  EXTREME: '#8b0000'
};

const PropertyEvaluation = () => {
  const { user } = useAuth();
  const lowDataMode = user?.preferences?.lowDataMode || false;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [commodityRates, setCommodityRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  // Search for locations via Google Places
  const searchLocations = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const result = await satelliteApi.searchLocations(query);
      if (result.success && result.predictions) {
        setSearchResults(result.predictions);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search — single shared timer per input so we don't spam the API
  // on every keystroke. The previous implementation returned a cleanup from
  // an onChange handler, which does nothing — every keystroke fired a request.
  const searchTimerRef = useRef(null);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocations(value), 350);
  };

  // Cancel any pending search timer when the component unmounts.
  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  // Select a location from search
  const handleLocationSelect = async (prediction) => {
    setSearchResults([]);
    setSearchQuery(prediction.description);
    try {
      const placeId = prediction.placeId || prediction.place_id;
      const details = await satelliteApi.getPlaceDetails(placeId);
      if (details.success && details.location) {
        const loc = {
          lat: details.location.lat,
          lng: details.location.lng,
          name: details.location.name || prediction.description,
          address: prediction.description
        };
        setSelectedLocation(loc);
        runEvaluation(loc);
      }
    } catch (err) {
      console.error('Place details error:', err);
      toast.error('Failed to get location details');
    }
  };

  // Run the full property evaluation
  const runEvaluation = async (location) => {
    setLoading(true);
    setEvaluation(null);
    setCommodityRates(null);
    try {
      const [evalResult, ratesResult] = await Promise.all([
        satelliteApi.evaluateProperty(location.lat, location.lng, location.address),
        satelliteApi.getCommodityRates(location.lat, location.lng)
      ]);

      if (evalResult.success) {
        setEvaluation(evalResult);
        toast.success('Property evaluation complete');
      } else {
        toast.error(evalResult.error || 'Evaluation failed');
      }

      if (ratesResult.success) {
        setCommodityRates(ratesResult);
      }
    } catch (err) {
      console.error('Evaluation error:', err);
      toast.error('Failed to evaluate property');
    } finally {
      setLoading(false);
    }
  };

  const getRiskGauge = (score, label) => {
    const level = score >= 70 ? 'HIGH' : score >= 40 ? 'MODERATE' : 'LOW';
    const color = RISK_COLORS[level] || RISK_COLORS.LOW;
    return (
      <div className="risk-gauge">
        <div className="gauge-bar">
          <div className="gauge-fill" style={{ width: `${Math.min(score, 100)}%`, background: color }} />
        </div>
        <div className="gauge-info">
          <span className="gauge-label">{label}</span>
          <span className="gauge-score" style={{ color }}>{score}/100 — {level}</span>
        </div>
      </div>
    );
  };

  const renderOverview = () => {
    if (!evaluation) return null;
    const { riskProfile, location, airQuality } = evaluation;

    return (
      <div className="eval-section">
        <h3><FiShield /> Overall Risk Profile</h3>
        <div className="risk-overview-card">
          <div className="overall-risk" style={{ borderColor: RISK_COLORS[riskProfile.overall.level] }}>
            <div className="overall-score" style={{ color: RISK_COLORS[riskProfile.overall.level] }}>
              {riskProfile.overall.score}
            </div>
            <div className="overall-label">{riskProfile.overall.level} RISK</div>
          </div>
          <div className="risk-gauges">
            {getRiskGauge(riskProfile.wildfire.score, '🔥 Wildfire Risk')}
            {getRiskGauge(riskProfile.flood.score, '🌊 Flood Risk')}
            {getRiskGauge(riskProfile.weather.score, '🌡️ Weather Risk')}
          </div>
        </div>

        <div className="location-details-card">
          <h4><FiMapPin /> Location Details</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Address</span>
              <span className="detail-value">{location.address}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Coordinates</span>
              <span className="detail-value">{location.lat.toFixed(4)}°, {location.lng.toFixed(4)}°</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Elevation</span>
              <span className="detail-value">{location.elevation != null ? `${Math.round(location.elevation)}m` : 'N/A'}</span>
            </div>
            {airQuality && (
              <div className="detail-item">
                <span className="detail-label">Air Quality (AQI)</span>
                <span className="detail-value">{airQuality.aqi || 'N/A'} | PM2.5: {airQuality.pm25 || 'N/A'}</span>
              </div>
            )}
          </div>
        </div>

        <div className="data-sources-card">
          <h4><FiInfo /> Data Sources (All Live)</h4>
          <div className="source-tags">
            {Object.entries(evaluation.dataSources).map(([key, value]) => (
              <span key={key} className="source-tag">
                {key}: <strong>{value}</strong>
              </span>
            ))}
          </div>
          <p className="timestamp">Analysis performed: {new Date(evaluation.timestamp).toLocaleString()}</p>
        </div>
      </div>
    );
  };

  const renderWildfireRisk = () => {
    if (!evaluation) return null;
    const { wildfire } = evaluation.riskProfile;

    return (
      <div className="eval-section">
        <h3>🔥 Wildfire Risk Assessment</h3>
        <div className="risk-detail-card" style={{ borderLeftColor: RISK_COLORS[wildfire.level] }}>
          <div className="risk-header">
            <span className="risk-level-badge" style={{ background: RISK_COLORS[wildfire.level] }}>
              {wildfire.level}
            </span>
            <span className="risk-score-lg">{wildfire.score}/100</span>
          </div>

          <div className="stat-grid">
            <div className="stat-box">
              <div className="stat-number">{wildfire.nearbyFires}</div>
              <div className="stat-label">Active Fires (100km)</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{wildfire.avgBrightness ? `${wildfire.avgBrightness}K` : 'N/A'}</div>
              <div className="stat-label">Avg Brightness Temp</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{wildfire.dataSource}</div>
              <div className="stat-label">Data Source</div>
            </div>
          </div>

          {wildfire.recentFires.length > 0 && (
            <div className="recent-fires">
              <h4>Most Recent Fire Detections</h4>
              <table className="mini-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Satellite</th>
                    <th>Brightness</th>
                    <th>FRP (MW)</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {wildfire.recentFires.map((fire, i) => (
                    <tr key={i}>
                      <td>{fire.date || 'N/A'}</td>
                      <td>{fire.satellite || 'VIIRS'}</td>
                      <td>{fire.brightness ? `${fire.brightness.toFixed(1)}K` : 'N/A'}</td>
                      <td>{fire.frp ? fire.frp.toFixed(1) : 'N/A'}</td>
                      <td>{fire.lat?.toFixed(2)}°, {fire.lng?.toFixed(2)}°</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFloodRisk = () => {
    if (!evaluation) return null;
    const { flood } = evaluation.riskProfile;

    return (
      <div className="eval-section">
        <h3>🌊 Flood Risk Assessment</h3>
        <div className="risk-detail-card" style={{ borderLeftColor: RISK_COLORS[flood.level] }}>
          <div className="risk-header">
            <span className="risk-level-badge" style={{ background: RISK_COLORS[flood.level] }}>
              {flood.level}
            </span>
            <span className="risk-score-lg">{flood.score}/100</span>
          </div>

          <div className="stat-grid">
            <div className="stat-box">
              <div className="stat-number">{flood.elevation != null ? `${Math.round(flood.elevation)}m` : 'N/A'}</div>
              <div className="stat-label">Elevation</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{flood.floodIndex?.index || '—'}</div>
              <div className="stat-label">Flood Index</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{flood.floodIndex?.riskLevel || flood.details?.riskLevel || flood.level}</div>
              <div className="stat-label">Flood Zone</div>
            </div>
          </div>

          <div className="risk-factors">
            <h4>Contributing Factors</h4>
            <ul>
              {flood.elevation != null && flood.elevation < 50 && (
                <li className="factor-warning">⚠️ Low elevation ({Math.round(flood.elevation)}m) increases flood exposure</li>
              )}
              {flood.recommendations?.length > 0 && flood.recommendations.map((rec, i) => (
                <li key={i} className="factor-info">📋 {rec}</li>
              ))}
              {flood.score < 30 && (
                <li className="factor-ok">✅ Location has low flood risk based on elevation and proximity</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderWeatherRisk = () => {
    if (!evaluation) return null;
    const { weather } = evaluation.riskProfile;

    return (
      <div className="eval-section">
        <h3><FiThermometer /> Current Weather Conditions</h3>
        <div className="weather-conditions-card">
          <div className="stat-grid">
            <div className="stat-box">
              <div className="stat-number">{weather.current.temp != null ? `${Math.round(weather.current.temp)}°C` : 'N/A'}</div>
              <div className="stat-label">Temperature</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{weather.current.humidity != null ? `${weather.current.humidity}%` : 'N/A'}</div>
              <div className="stat-label">Humidity</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{weather.current.windSpeed != null ? `${weather.current.windSpeed} m/s` : 'N/A'}</div>
              <div className="stat-label">Wind Speed</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{weather.current.conditions || 'N/A'}</div>
              <div className="stat-label">Conditions</div>
            </div>
          </div>
          {getRiskGauge(weather.score, 'Weather Risk Score')}
        </div>
      </div>
    );
  };

  const formatZAR = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `R ${Number(value).toLocaleString('en-ZA')}`;
  };

  const renderAIValuation = () => {
    if (!evaluation?.aiValuation) return null;
    const { aiValuation } = evaluation;
    const a = aiValuation.analysis;

    // Handle raw text (unparsed) response
    if (a?.rawAnalysis) {
      return (
        <div className="eval-section">
          <h3>🤖 AI Property Valuation (Gemini)</h3>
          <div className="ai-raw-analysis"><pre>{a.rawAnalysis}</pre></div>
        </div>
      );
    }

    const valuation = a?.infrastructureValuation;
    const construction = a?.constructionAnalysis;
    const property = a?.propertyIdentification;
    const damage = a?.damageAssessment;
    const risk = a?.riskFactors;
    const confidence = a?.confidenceScore;

    return (
      <div className="eval-section">
        <h3>🤖 AI Property Valuation & Infrastructure Analysis</h3>
        {aiValuation.isMockData && (
          <div className="mock-warning">
            ⚠️ AI analysis is using estimated data. Enable the Gemini API or set GEMINI_API_KEY for live AI-powered analysis. All satellite data (weather, fires, floods) is real.
          </div>
        )}

        {/* Satellite & Street View Images */}
        {aiValuation.images && (
          <div className="ai-images">
            <div className="ai-image">
              <img src={aiValuation.images.satellite} alt="Satellite view" onError={(e) => { e.target.style.display = 'none'; }} />
              <span>📡 Satellite View</span>
            </div>
            <div className="ai-image">
              <img src={aiValuation.images.streetView} alt="Street view" onError={(e) => { e.target.style.display = 'none'; }} />
              <span>🏠 Street View</span>
            </div>
          </div>
        )}

        {/* ═══ TOTAL PROPERTY VALUE HERO ═══ */}
        {valuation && (
          <div className="valuation-hero">
            <div className="hero-label">Estimated Total Property Value</div>
            <div className="hero-price">{formatZAR(valuation.totalPropertyValue)}</div>
            <div className="hero-confidence">
              Confidence: <span className={`conf-${(valuation.confidenceLevel || '').toLowerCase()}`}>{valuation.confidenceLevel || 'N/A'}</span>
            </div>
          </div>
        )}

        {/* ═══ PROPERTY IDENTIFICATION ═══ */}
        {property && (
          <div className="val-card">
            <h4>🏗️ Property Identification</h4>
            <div className="detail-grid four-col">
              <div className="detail-item">
                <span className="detail-label">Property Type</span>
                <span className="detail-value highlight">{property.propertyType}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Land Size</span>
                <span className="detail-value">{property.estimatedLandSizeM2?.toLocaleString() || '—'} m²</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Building Size</span>
                <span className="detail-value">{property.estimatedBuildingSizeM2?.toLocaleString() || '—'} m²</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Structures</span>
                <span className="detail-value">{property.numberOfStructures || '—'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Stories</span>
                <span className="detail-value">{property.buildingStories || '—'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Year Built (est.)</span>
                <span className="detail-value">{property.yearBuiltEstimate || '—'}</span>
              </div>
              <div className="detail-item span-2">
                <span className="detail-label">Neighborhood</span>
                <span className="detail-value">{property.neighborhood || '—'}</span>
              </div>
            </div>
          </div>
        )}

        {/* ═══ CONSTRUCTION MATERIALS ANALYSIS ═══ */}
        {construction && (
          <div className="val-card">
            <h4>🧱 Construction & Materials Analysis</h4>
            <div className="materials-grid">
              <div className="material-item">
                <div className="material-icon">🏠</div>
                <div className="material-info">
                  <div className="material-label">Roof</div>
                  <div className="material-value">{construction.roofType}</div>
                  <div className={`material-condition cond-${(construction.roofCondition || '').toLowerCase()}`}>{construction.roofCondition}</div>
                </div>
              </div>
              <div className="material-item">
                <div className="material-icon">🧱</div>
                <div className="material-info">
                  <div className="material-label">Walls</div>
                  <div className="material-value">{construction.wallMaterial}</div>
                  <div className={`material-condition cond-${(construction.wallCondition || '').toLowerCase()}`}>{construction.wallCondition}</div>
                </div>
              </div>
              <div className="material-item">
                <div className="material-icon">🪨</div>
                <div className="material-info">
                  <div className="material-label">Foundation</div>
                  <div className="material-value">{construction.foundationType}</div>
                </div>
              </div>
              <div className="material-item">
                <div className="material-icon">🪟</div>
                <div className="material-info">
                  <div className="material-label">Windows</div>
                  <div className="material-value">{construction.windowType}</div>
                </div>
              </div>
            </div>
            <div className="finish-quality-bar">
              <span className="fq-label">Finish Quality:</span>
              <div className="fq-track">
                {['Economy', 'Standard', 'Above Average', 'Luxury'].map(level => (
                  <span key={level} className={`fq-step ${(construction.finishQuality || '').toLowerCase() === level.toLowerCase() ? 'fq-active' : ''}`}>
                    {level}
                  </span>
                ))}
              </div>
            </div>
            <div className="overall-condition-badge">
              Overall Condition: <span className={`cond-badge cond-${(construction.overallCondition || '').toLowerCase()}`}>{construction.overallCondition}</span>
            </div>
          </div>
        )}

        {/* ═══ INFRASTRUCTURE VALUATION BREAKDOWN ═══ */}
        {valuation && (
          <div className="val-card valuation-breakdown-card">
            <h4>💰 Infrastructure Valuation — Step-by-Step Calculation</h4>

            {/* Step 1: Land Value */}
            <div className="calc-step">
              <div className="step-header">
                <span className="step-number">1</span>
                <span className="step-title">Land Value</span>
                <span className="step-total">{formatZAR(valuation.landValue?.totalLandValue)}</span>
              </div>
              <div className="step-detail">
                <div className="calc-formula">
                  <span className="calc-var">{property?.estimatedLandSizeM2?.toLocaleString() || '—'} m²</span>
                  <span className="calc-op">×</span>
                  <span className="calc-var">{formatZAR(valuation.landValue?.ratePerM2)}/m²</span>
                  <span className="calc-op">=</span>
                  <span className="calc-result">{formatZAR(valuation.landValue?.totalLandValue)}</span>
                </div>
                <p className="calc-methodology">{valuation.landValue?.methodology}</p>
              </div>
            </div>

            {/* Step 2: Main Structure */}
            <div className="calc-step">
              <div className="step-header">
                <span className="step-number">2</span>
                <span className="step-title">Main Structure</span>
                <span className="step-total">{formatZAR(valuation.mainStructure?.totalValue)}</span>
              </div>
              <div className="step-detail">
                <div className="calc-formula">
                  <span className="calc-var">{property?.estimatedBuildingSizeM2?.toLocaleString() || '—'} m²</span>
                  <span className="calc-op">×</span>
                  <span className="calc-var">{formatZAR(valuation.mainStructure?.ratePerM2)}/m²</span>
                  <span className="calc-op">=</span>
                  <span className="calc-result">{formatZAR(valuation.mainStructure?.totalValue)}</span>
                </div>
                <p className="calc-methodology">{valuation.mainStructure?.methodology}</p>
              </div>
            </div>

            {/* Step 3: Additional Structures */}
            <div className="calc-step">
              <div className="step-header">
                <span className="step-number">3</span>
                <span className="step-title">Additional Structures</span>
                <span className="step-total">{formatZAR(valuation.additionalStructures?.totalValue)}</span>
              </div>
              <div className="step-detail">
                {valuation.additionalStructures?.breakdown?.length > 0 ? (
                  <table className="breakdown-table">
                    <thead><tr><th>Item</th><th>Description</th><th>Value (ZAR)</th></tr></thead>
                    <tbody>
                      {valuation.additionalStructures.breakdown.map((item, i) => (
                        <tr key={i}>
                          <td className="bt-item">{item.item}</td>
                          <td className="bt-desc">{item.description}</td>
                          <td className="bt-val">{formatZAR(item.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="calc-methodology">No additional structures identified</p>
                )}
              </div>
            </div>

            {/* Step 4: Improvements */}
            <div className="calc-step">
              <div className="step-header">
                <span className="step-number">4</span>
                <span className="step-title">Improvements & Features</span>
                <span className="step-total">{formatZAR(valuation.improvements?.totalValue)}</span>
              </div>
              <div className="step-detail">
                {valuation.improvements?.breakdown?.length > 0 ? (
                  <table className="breakdown-table">
                    <thead><tr><th>Item</th><th>Description</th><th>Value (ZAR)</th></tr></thead>
                    <tbody>
                      {valuation.improvements.breakdown.map((item, i) => (
                        <tr key={i}>
                          <td className="bt-item">{item.item}</td>
                          <td className="bt-desc">{item.description}</td>
                          <td className="bt-val">{formatZAR(item.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="calc-methodology">No specific improvements identified</p>
                )}
              </div>
            </div>

            {/* Summary Bar Chart */}
            <div className="valuation-summary">
              <h5>📊 Value Composition</h5>
              <div className="summary-bars">
                {valuation.valuationSummary?.map((item, i) => {
                  const colors = ['#3182ce', '#38a169', '#dd6b20', '#805ad5'];
                  return (
                    <div key={i} className="summary-bar-row">
                      <span className="sb-label">{item.component}</span>
                      <div className="sb-track">
                        <div className="sb-fill" style={{ width: `${Math.max(item.percentage, 2)}%`, background: colors[i % colors.length] }} />
                      </div>
                      <span className="sb-value">{formatZAR(item.value)}</span>
                      <span className="sb-pct">{item.percentage}%</span>
                    </div>
                  );
                })}
              </div>
              <div className="summary-total-row">
                <span>Total Estimated Value</span>
                <span className="summary-grand-total">{formatZAR(valuation.totalPropertyValue)}</span>
              </div>
              {valuation.valuationNotes && (
                <p className="valuation-notes">📝 {valuation.valuationNotes}</p>
              )}
            </div>
          </div>
        )}

        {/* ═══ DAMAGE ASSESSMENT ═══ */}
        {damage && (
          <div className="val-card">
            <h4>⚠️ Damage Assessment</h4>
            {damage.currentDamageObserved ? (
              <>
                <div className="damage-header">
                  <span className="damage-type">{damage.damageType}</span>
                  <span className="damage-severity">Severity: {damage.severityScale}/10</span>
                </div>
                <div className="stat-grid">
                  <div className="stat-box">
                    <div className="stat-number">{damage.affectedAreaPercentage}%</div>
                    <div className="stat-label">Area Affected</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-number">{formatZAR(damage.estimatedRepairCost)}</div>
                    <div className="stat-label">Est. Repair Cost</div>
                  </div>
                </div>
                {damage.repairItems?.length > 0 && (
                  <table className="breakdown-table">
                    <thead><tr><th>Repair Item</th><th>Cost</th><th>Urgency</th></tr></thead>
                    <tbody>
                      {damage.repairItems.map((item, i) => (
                        <tr key={i}>
                          <td className="bt-item">{item.item}</td>
                          <td className="bt-val">{formatZAR(item.cost)}</td>
                          <td><span className={`urgency-badge urg-${(item.urgency || '').toLowerCase().replace(/\s+/g, '-')}`}>{item.urgency}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : (
              <div className="no-damage">
                <span className="no-damage-icon">✅</span>
                <p>No significant damage observed from satellite and street view imagery.</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ RISK FACTORS ═══ */}
        {risk && (
          <div className="val-card">
            <h4>🛡️ Risk Factors & Insurance</h4>
            <div className="risk-columns">
              <div className="risk-col risk-negative">
                <h5>⚠️ Environmental Risks</h5>
                <ul>{risk.environmentalRisks?.map((r, i) => <li key={i}>{r}</li>)}</ul>
                {risk.structuralConcerns?.length > 0 && (
                  <>
                    <h5>🔧 Structural Concerns</h5>
                    <ul>{risk.structuralConcerns.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </>
                )}
              </div>
              <div className="risk-col risk-positive">
                <h5>✅ Positive Features</h5>
                <ul>{risk.positiveFeatures?.map((r, i) => <li key={i}>{r}</li>)}</ul>
                <h5>📋 Insurance Recommendations</h5>
                <ul>{risk.insuranceRecommendations?.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </div>
            </div>
          </div>
        )}

        {/* ═══ CONFIDENCE SCORE ═══ */}
        {confidence && (
          <div className="val-card">
            <h4>📊 Analysis Confidence</h4>
            <div className="confidence-meter">
              <div className="confidence-fill" style={{ width: `${confidence.percentage || 0}%` }} />
              <span>{confidence.percentage || 0}%</span>
            </div>
            <div className="confidence-details">
              <div className="conf-detail">
                <span className="conf-label">Image Quality:</span>
                <span>{confidence.imageQualityAssessment || 'N/A'}</span>
              </div>
              {confidence.limitationsIdentified?.length > 0 && (
                <div className="conf-detail">
                  <span className="conf-label">Limitations:</span>
                  <ul>{confidence.limitationsIdentified.map((l, i) => <li key={i}>{l}</li>)}</ul>
                </div>
              )}
              {confidence.recommendedVerification?.length > 0 && (
                <div className="conf-detail">
                  <span className="conf-label">Recommended Verification:</span>
                  <ul>{confidence.recommendedVerification.map((v, i) => <li key={i}>{v}</li>)}</ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ METHODOLOGY ═══ */}
        <div className="ai-methodology">
          <h4><FiInfo /> How This Valuation Works</h4>
          <ol>
            <li><strong>Satellite Imagery:</strong> Google Static Maps provides high-res satellite view. AI identifies property boundaries, structures, and land use.</li>
            <li><strong>Street View Analysis:</strong> Google Street View imagery analyzed for building materials, roof type, wall construction, and finish quality.</li>
            <li><strong>Construction Cost Estimation:</strong> AI applies current South African building cost rates (per m²) based on identified materials and finish quality.</li>
            <li><strong>Land Valuation:</strong> Location-based land rate per m² estimated from neighborhood context, accessibility, and surrounding development.</li>
            <li><strong>Additional Structures:</strong> Outbuildings, walls, garages, pools identified from satellite view and valued separately.</li>
            <li><strong>Improvements:</strong> Paving, landscaping, security features, solar panels, and other improvements identified and costed.</li>
            <li><strong>Risk Adjustment:</strong> Fire ({evaluation.riskProfile?.wildfire?.nearbyFires || 0} active fires), flood, and weather conditions factor into insurance recommendations.</li>
            <li><strong>AI Engine:</strong> Google Gemini 2.0 Flash processes all data to produce the final comprehensive valuation.</li>
          </ol>
        </div>
      </div>
    );
  };

  const renderCommodityRates = () => {
    if (!commodityRates) return null;

    const hasRelevantCommodities = commodityRates.rates && Object.keys(commodityRates.rates).length > 0;
    const hasReferenceRates = commodityRates.referenceRates && Object.keys(commodityRates.referenceRates).length > 0;
    const landUse = commodityRates.landUse;
    const relevance = commodityRates.relevance;

    const getLandUseIcon = (type) => {
      const icons = { URBAN: '🏙️', SUBURBAN: '🏘️', PERI_URBAN: '🏡', AGRICULTURAL: '🌾', PASTORAL: '🐄', RURAL: '🏕️', WILDERNESS: '🌿', UNKNOWN: '📍' };
      return icons[type] || '📍';
    };

    const getLandUseColor = (type) => {
      const colors = { URBAN: '#e53e3e', SUBURBAN: '#dd6b20', PERI_URBAN: '#d69e2e', AGRICULTURAL: '#38a169', PASTORAL: '#319795', RURAL: '#3182ce', WILDERNESS: '#805ad5' };
      return colors[type] || '#718096';
    };

    const getCommodityIcon = (name) => {
      const icons = { maize: '🌽', wheat: '🌾', cattle: '🐄', sheep: '🐑', sunflower: '🌻', soybeans: '🫘', tractors: '🚜', goats: '🐐' };
      return icons[name] || '📦';
    };

    return (
      <div className="eval-section">
        <h3><FiTrendingUp /> Satellite-Based Commodity Analysis</h3>

        {/* Land Use Classification Card */}
        {landUse && (
          <div className="land-use-card" style={{ borderLeftColor: getLandUseColor(landUse.type) }}>
            <div className="land-use-header">
              <span className="land-use-icon">{getLandUseIcon(landUse.type)}</span>
              <div className="land-use-info">
                <div className="land-use-type" style={{ color: getLandUseColor(landUse.type) }}>
                  {landUse.type.replace(/_/g, '-')} ZONE
                </div>
                <div className="land-use-desc">{landUse.description}</div>
              </div>
              {landUse.confidence > 0 && (
                <div className="land-use-confidence">
                  <small>Confidence</small>
                  <span>{landUse.confidence}%</span>
                </div>
              )}
            </div>

            <div className="land-use-indicators">
              {landUse.vegetationIndex !== null && landUse.vegetationIndex !== undefined && (
                <div className="indicator">
                  <span className="indicator-icon">🌿</span>
                  <div>
                    <small>Vegetation Index</small>
                    <strong>{(landUse.vegetationIndex * 100).toFixed(0)}%</strong>
                  </div>
                </div>
              )}
              {landUse.soilMoisture !== null && landUse.soilMoisture !== undefined && (
                <div className="indicator">
                  <span className="indicator-icon">💧</span>
                  <div>
                    <small>Soil Moisture</small>
                    <strong>{(landUse.soilMoisture * 100).toFixed(0)}%</strong>
                  </div>
                </div>
              )}
              {landUse.agriculturalRadius !== null && landUse.agriculturalRadius !== undefined && landUse.agriculturalRadius > 0 && (
                <div className="indicator">
                  <span className="indicator-icon">🚜</span>
                  <div>
                    <small>Nearest Agriculture</small>
                    <strong>{landUse.agriculturalRadius} km</strong>
                  </div>
                </div>
              )}
            </div>

            {landUse.nearbyLandUse && landUse.nearbyLandUse.length > 0 && (
              <div className="nearby-features">
                <small>Nearby agricultural features:</small>
                <div className="feature-tags">
                  {landUse.nearbyLandUse.map((f, i) => (
                    <span key={i} className="feature-tag">
                      📍 {f.name} ({f.distance} km)
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Satellite Conditions */}
        <div className="commodity-conditions">
          <span>🌡️ {commodityRates.conditions.temperature}°C</span>
          <span>💧 {commodityRates.conditions.humidity}%</span>
          <span>🔥 {commodityRates.conditions.nearbyFires} nearby fires</span>
        </div>

        {/* Relevance Notice */}
        {relevance && (
          <div className={`relevance-notice ${hasRelevantCommodities ? 'has-commodities' : 'no-commodities'}`}>
            <div className="relevance-icon">{hasRelevantCommodities ? '✅' : 'ℹ️'}</div>
            <div className="relevance-text">
              <strong>{relevance.reason}</strong>
              {relevance.suggestion && <p>{relevance.suggestion}</p>}
            </div>
          </div>
        )}

        {/* Relevant Commodities (applies to this location) */}
        {hasRelevantCommodities && (
          <>
            <h4 className="commodity-section-title">📊 Commodities Applicable to This Location</h4>
            <div className="commodity-rates-grid">
              {Object.entries(commodityRates.rates).map(([name, data]) => (
                <div key={name} className={`commodity-rate-card applicable ${data.isLivePrice ? 'live-price' : ''}`}>
                  <div className="commodity-card-header">
                    <span className="commodity-emoji">{getCommodityIcon(name)}</span>
                    <div className="commodity-name">
                      {name.charAt(0).toUpperCase() + name.slice(1)}
                      {data.isLivePrice && <span className="live-badge" title="Live price from API">🟢 LIVE</span>}
                    </div>
                  </div>
                  <div className="commodity-price">
                    R {data.adjustedRate.toLocaleString()} <small>/{data.unit}</small>
                  </div>
                  {data.multiplier !== 1 && (
                    <div className={`commodity-adjustment ${data.multiplier > 1 ? 'up' : 'down'}`}>
                      {data.multiplier > 1 ? '▲' : '▼'} {Math.abs((data.multiplier - 1) * 100).toFixed(0)}%
                      <span className="base-rate">Base: R {data.baseRate.toLocaleString()}</span>
                    </div>
                  )}
                  {data.multiplier === 1 && (
                    <div className="commodity-adjustment neutral">
                      — No adjustment
                      <span className="base-rate">Base: R {data.baseRate.toLocaleString()}</span>
                    </div>
                  )}
                  {data.factors.length > 0 && (
                    <div className="commodity-factors">
                      {data.factors.map((f, i) => (
                        <small key={i}>{f}</small>
                      ))}
                    </div>
                  )}
                  <div className="commodity-source">
                    <small title={`Last updated: ${data.priceLastUpdated || 'N/A'}`}>
                      📊 {data.priceSource || 'Market data'}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Reference Market Rates (shown for context in urban/suburban) */}
        {hasReferenceRates && (
          <>
            <h4 className="commodity-section-title reference-title">
              📋 Regional SAFEX Market Rates (Reference Only)
            </h4>
            <p className="reference-subtitle">
              These are regional market rates and do <strong>not</strong> represent commodities at this location.
              Shown for insurance and valuation reference only.
            </p>
            <div className="commodity-rates-grid reference-grid">
              {Object.entries(commodityRates.referenceRates).map(([name, data]) => (
                <div key={name} className="commodity-rate-card reference-card">
                  <div className="commodity-card-header">
                    <span className="commodity-emoji">{getCommodityIcon(name)}</span>
                    <div className="commodity-name">
                      {name.charAt(0).toUpperCase() + name.slice(1)}
                      <span className="ref-badge">REF</span>
                    </div>
                  </div>
                  <div className="commodity-price ref-price">
                    R {data.baseRate.toLocaleString()} <small>/{data.unit}</small>
                  </div>
                  <div className="commodity-source">
                    <small>{data.priceSource || 'SAFEX market rate'}</small>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* No commodities at all */}
        {!hasRelevantCommodities && !hasReferenceRates && (
          <div className="no-commodities-state">
            <span className="no-comm-icon">🏙️</span>
            <h4>No Agricultural Commodities Detected</h4>
            <p>Satellite analysis indicates this is a non-agricultural area. No commodity rates are applicable.</p>
          </div>
        )}

        <div className="commodity-disclaimer">
          <FiInfo size={14} />
          <small>
            Land use classified via Google Geocoding and Open-Meteo satellite vegetation data.
            Base prices from SAFEX/JSE contracts. Adjustments from NASA FIRMS fire data and live weather.
          </small>
        </div>
      </div>
    );
  };

  return (
    <div className="property-evaluation-page">
      <div className="page-header">
        <div className="page-title">
          <h2><FiMapPin /> Property Evaluation</h2>
          <p>Search any location to get a comprehensive satellite-based risk assessment and AI property valuation</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="property-search-bar">
        <div className="search-input-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search for an address, city, or location..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="property-search-input"
          />
          {isSearching && <span className="search-spinner">⏳</span>}
        </div>
        {searchResults.length > 0 && (
          <ul className="property-search-results">
            {searchResults.map((result, index) => (
              <li
                key={result.placeId || result.place_id || index}
                onClick={() => handleLocationSelect(result)}
              >
                <FiMapPin className="result-pin" />
                <span>{result.description}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Selected Location Banner */}
      {selectedLocation && (
        <div className="selected-location-banner">
          <FiMapPin />
          <span>{selectedLocation.address || selectedLocation.name}</span>
          <span className="coords">({selectedLocation.lat.toFixed(4)}°, {selectedLocation.lng.toFixed(4)}°)</span>
          {!loading && evaluation && (
            <button className="refresh-eval-btn" onClick={() => runEvaluation(selectedLocation)}>
              <FiRefreshCw /> Re-evaluate
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="eval-loading">
          <div className="loading-spinner"></div>
          <h3>Analyzing Property...</h3>
          <p>Fetching satellite data from NASA FIRMS, Google Weather, Elevation APIs, and running AI analysis...</p>
        </div>
      )}

      {/* Results */}
      {evaluation && !loading && (
        <>
          {/* Section Navigation */}
          <div className="eval-nav">
            <button className={activeSection === 'overview' ? 'active' : ''} onClick={() => setActiveSection('overview')}>
              <FiShield /> Overview
            </button>
            <button className={activeSection === 'wildfire' ? 'active' : ''} onClick={() => setActiveSection('wildfire')}>
              🔥 Wildfire
            </button>
            <button className={activeSection === 'flood' ? 'active' : ''} onClick={() => setActiveSection('flood')}>
              🌊 Flood
            </button>
            <button className={activeSection === 'weather' ? 'active' : ''} onClick={() => setActiveSection('weather')}>
              <FiThermometer /> Weather
            </button>
            <button className={activeSection === 'ai' ? 'active' : ''} onClick={() => setActiveSection('ai')}>
              🤖 AI Valuation
            </button>
            <button className={activeSection === 'commodities' ? 'active' : ''} onClick={() => setActiveSection('commodities')}>
              <FiTrendingUp /> Commodities
            </button>
          </div>

          {/* Section Content */}
          <div className="eval-content">
            {activeSection === 'overview' && renderOverview()}
            {activeSection === 'wildfire' && renderWildfireRisk()}
            {activeSection === 'flood' && renderFloodRisk()}
            {activeSection === 'weather' && renderWeatherRisk()}
            {activeSection === 'ai' && renderAIValuation()}
            {activeSection === 'commodities' && renderCommodityRates()}
          </div>
        </>
      )}

      {/* Empty State */}
      {!selectedLocation && !loading && (
        <div className="eval-empty-state">
          <div className="empty-icon">🛰️</div>
          <h3>Search a Location to Begin</h3>
          <p>Enter an address or location above. The system will analyze:</p>
          <div className="empty-features">
            <div className="empty-feature">
              <span>🔥</span>
              <strong>Wildfire Risk</strong>
              <small>NASA FIRMS satellite fire detection within 100km</small>
            </div>
            <div className="empty-feature">
              <span>🌊</span>
              <strong>Flood Risk</strong>
              <small>Elevation analysis, water body proximity</small>
            </div>
            <div className="empty-feature">
              <span>🌡️</span>
              <strong>Weather Conditions</strong>
              <small>Live temperature, humidity, wind from Google Weather</small>
            </div>
            <div className="empty-feature">
              <span>🤖</span>
              <strong>AI Valuation</strong>
              <small>Google Gemini analyses satellite + Street View imagery</small>
            </div>
            <div className="empty-feature">
              <span>📊</span>
              <strong>Commodity Impact</strong>
              <small>Satellite-adjusted market rates for local conditions</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyEvaluation;
